import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import tokens from '../theme/tokens';

// 현황 카드 — 머리글(지금 상태) · 출발→도착 거리 · 남은 시간/거리 · 구간 띠(배차 → 픽업 이동 → 픽업 → 주행, 지난 구간은 채움·지금 구간은 진하게) · 도착 예상 범위.
// 구간 숫자·도착 예상은 서버 routePlan(접수 폼 [경로 상세보기]와 같은 규칙)이 준다. 단계별 실제 시각은 아래 접수 정보 카드에
const useStyles = makeStyles(() => ({
  root: {
    padding: '22px 20px 18px',
    borderRadius: tokens.radiusLg,
    backgroundColor: tokens.surface,
    boxShadow: tokens.cardShadow,
  },
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 },
  chip: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    backgroundColor: tokens.primarySoft,
    color: tokens.primaryDark,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: -0.3,
  },
  chipMuted: { backgroundColor: tokens.surfaceSoft, color: tokens.muted },
  dist: {
    fontSize: 12,
    fontWeight: 700,
    color: tokens.primaryDark,
    padding: '3px 10px',
    borderRadius: 12,
    border: `1px solid ${tokens.primaryLine}`,
    backgroundColor: tokens.surface,
    whiteSpace: 'nowrap',
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.35,
    letterSpacing: -1,
    color: tokens.ink,
    wordBreak: 'keep-all',
  },
  sub: {
    margin: '6px 0 0',
    fontSize: 14,
    lineHeight: 1.6,
    letterSpacing: -0.4,
    color: tokens.text,
    wordBreak: 'keep-all',
  },
  figures: { display: 'flex', gap: 10, marginTop: 16 },
  figure: {
    flex: '1 1 0',
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: tokens.primarySoft,
    '& small': { display: 'block', fontSize: 12, color: tokens.muted, letterSpacing: -0.4 },
    '& b': { display: 'block', marginTop: 4, fontSize: 22, fontWeight: 700, letterSpacing: -1, color: tokens.primaryDark, lineHeight: 1.2 },
    '& b span': { fontSize: 13, marginLeft: 2, color: tokens.text, fontWeight: 500 },
  },
  // 구간 띠와 이름 줄은 같은 격자(gridTemplateColumns)를 쓴다 — 구간 길이는 시간 비율이되 칸마다 최소 폭을 두어 좁은 화면에서도 이름이 겹치지 않는다
  track: { display: 'grid', gap: 3, height: 18, marginTop: 18, borderRadius: 7, overflow: 'hidden' },
  seg: { minWidth: 0, borderRadius: 5, backgroundColor: tokens.primaryLine },
  segDone: { backgroundColor: tokens.primary },
  segNow: { backgroundImage: 'linear-gradient(90deg, #ff925c, #f77c40)', boxShadow: `inset 0 0 0 2px ${tokens.surface}` },
  labels: { display: 'grid', gap: 3, marginTop: 7 },
  label: {
    minWidth: 0,
    textAlign: 'center',
    '& b': { display: 'block', fontSize: 11.5, fontWeight: 700, letterSpacing: -0.3, color: tokens.muted, lineHeight: 1.25, wordBreak: 'keep-all' },
    '& small': { display: 'block', marginTop: 2, fontSize: 11, color: tokens.placeholder, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis' },
    '@media (max-width: 380px)': {
      '& b': { fontSize: 10.5, letterSpacing: -0.4 },
      '& small': { fontSize: 10 },
    },
  },
  labelDone: { '& b': { color: tokens.text }, '& small': { color: tokens.muted } },
  labelNow: { '& b': { color: tokens.primaryDark }, '& small': { color: tokens.primaryDark } },
  arrival: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${tokens.lineSoft}`,
    fontSize: 13,
    color: tokens.text,
    letterSpacing: -0.4,
    '& b': { fontSize: 16, fontWeight: 700, color: tokens.primaryDark, letterSpacing: -0.6, fontVariantNumeric: 'tabular-nums' },
    '& small': { fontSize: 11.5, color: tokens.placeholder },
  },
}));

// 구간 칸 폭 — 시간 비율(fr)로 나누되 칸마다 최소 폭(전체의 1/n 의 80%)을 보장한다. 띠와 이름 줄이 같은 값을 써서 칸이 딱 맞는다
function segmentColumns(segments) {
  const n = Math.max(1, segments.length);
  const minPct = Math.floor((100 / n) * 0.8);
  return segments.map((s) => {
    const mid = s.min ? (s.min[0] + s.min[1]) / 2 : 10;
    return `minmax(${minPct}%, ${Math.max(6, Math.round(mid))}fr)`;
  }).join(' ');
}

const PHASE_CHIP = {
  waiting: '배차 중',
  reserved: '예약',
  to_pickup: '픽업지 이동',
  to_dest: '배송 중',
  done: '배송 완료',
  cancelled: '취소',
};

const fmtMin = (min) => (min == null ? null : min < 60 ? { v: min, u: '분' } : { v: `${Math.floor(min / 60)}시간 ${min % 60}`, u: '분' });
const fmtKm = (km) => (km == null ? null : km < 1 ? { v: Math.round(km * 1000), u: 'm' } : { v: km.toFixed(1), u: 'km' });
const minText = (m) => (m >= 60 ? `${Math.floor(m / 60)}시간${m % 60 ? ` ${m % 60}분` : ''}` : `${m}분`);
const rangeText = (range) => {
  if (!range) return '';
  const [lo, hi] = range;
  return lo === hi ? minText(lo) : `${minText(lo)}~${minText(hi)}`;
};
const hhmm = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function TrackStatus({ order }) {
  const cs = useStyles();
  const cancelled = order.phase === 'cancelled';
  const finished = order.phase === 'done' || cancelled;
  const eta = order.eta;
  const min = eta ? fmtMin(eta.min) : null;
  const km = eta ? fmtKm(eta.km) : null;
  const target = eta ? (eta.target === 'pickup' ? '픽업지까지' : '도착지까지') : '';
  const plan = !finished && order.plan && Array.isArray(order.plan.segments) ? order.plan : null;
  const dist = order.distance_km != null ? fmtKm(order.distance_km) : null;

  return (
    <section className={cs.root}>
      <div className={cs.top}>
        <span className={clsx(cs.chip, (cancelled || order.phase === 'reserved') && cs.chipMuted)}>{PHASE_CHIP[order.phase] || '배송 현황'}</span>
        {dist && !cancelled && <span className={cs.dist}>출발지 → 도착지 {dist.v}{dist.u}</span>}
      </div>
      <h1 className={cs.title}>{order.title}</h1>
      {order.sub && <p className={cs.sub}>{order.sub}</p>}

      {eta && (
        <div className={cs.figures}>
          <div className={cs.figure}>
            <small>{target} 남은 시간</small>
            <b>{min ? <>{min.v}<span>{min.u}</span></> : '-'}</b>
          </div>
          <div className={cs.figure}>
            <small>{target} 남은 거리</small>
            <b>{km ? <>{km.v}<span>{km.u}</span></> : '-'}</b>
          </div>
        </div>
      )}

      {plan && (
        <React.Fragment>
          <div className={cs.track} style={{ gridTemplateColumns: segmentColumns(plan.segments) }}>
            {plan.segments.map((s) => (
              <span key={s.key} className={clsx(cs.seg, s.state === 'done' && cs.segDone, s.state === 'now' && cs.segNow)} />
            ))}
          </div>
          <div className={cs.labels} style={{ gridTemplateColumns: segmentColumns(plan.segments) }}>
            {plan.segments.map((s) => (
              <span key={s.key} className={clsx(cs.label, s.state === 'done' && cs.labelDone, s.state === 'now' && cs.labelNow)}>
                <b>{s.label}</b>
                <small>{s.state === 'done' ? '완료' : s.min ? rangeText(s.min) : '-'}</small>
              </span>
            ))}
          </div>
          {plan.arrival && (
            <div className={cs.arrival}>
              <span>도착 예상 <b>{hhmm(plan.arrival[0])}{plan.arrival[1] && hhmm(plan.arrival[1]) !== hhmm(plan.arrival[0]) ? ` ~ ${hhmm(plan.arrival[1])}` : ''}</b></span>
              <small>남은 시간 {rangeText(plan.remaining)} · 교통 상황에 따라 달라질 수 있어요</small>
            </div>
          )}
        </React.Fragment>
      )}
    </section>
  );
}

export default TrackStatus;
